<template>
  <div class="login-container">
    <div class="login-background">
      <div class="gradient-overlay"></div>
      <div class="noise-overlay"></div>
    </div>

    <div class="reset-card">
      <div class="login-header">
        <div class="logo-section">
          <img src="@/assets/logo.png" alt="Koke Logo" class="logo-img" />
          <h1 class="brand-title">Koke Manga</h1>
        </div>
        <h2 class="login-title">{{ $t('manga.resetPassword.title') }}</h2>
        <p class="login-subtitle">{{ stepDesc }}</p>
      </div>

      <!-- 步骤指示器 -->
      <div class="step-indicator">
        <div class="step" :class="{ active: step >= 1, completed: step > 1 }">
          <div class="step-dot">1</div>
          <span>{{ $t('manga.resetPassword.step1') }}</span>
        </div>
        <div class="step-line" :class="{ active: step > 1 }"></div>
        <div class="step" :class="{ active: step >= 2, completed: step > 2 }">
          <div class="step-dot">2</div>
          <span>{{ $t('manga.resetPassword.step2') }}</span>
        </div>
        <div class="step-line" :class="{ active: step > 2 }"></div>
        <div class="step" :class="{ active: step >= 3 }">
          <div class="step-dot">3</div>
          <span>{{ $t('manga.resetPassword.step3') }}</span>
        </div>
      </div>

      <!-- 步骤1: 输入邮箱 -->
      <el-form v-if="step === 1" class="login-form" :model="emailForm" :rules="emailRules" ref="emailFormRef">
        <el-form-item prop="email">
          <el-input v-model="emailForm.email" :placeholder="$t('manga.resetPassword.emailPlaceholder')" clearable class="custom-input">
            <template #prefix>
              <i class="fas fa-envelope"></i>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item class="submit-item">
          <el-button class="submit-btn" type="primary" @click="handleSendCode" :loading="loading"
            :disabled="countdown > 0">
            {{ countdown > 0 ? `${countdown}s` : $t('manga.resetPassword.sendCode') }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 步骤2: 输入验证码 -->
      <el-form v-if="step === 2" class="login-form" :model="codeForm" :rules="codeRules" ref="codeFormRef">
        <el-form-item prop="code">
          <el-input v-model="codeForm.code" :placeholder="$t('manga.resetPassword.codePlaceholder')" clearable maxlength="6"
            class="custom-input" @keyup.enter="handleVerifyCode">
            <template #prefix>
              <i class="fas fa-shield-halved"></i>
            </template>
          </el-input>
        </el-form-item>

        <div class="code-hint">
          <p>{{ $t('manga.resetPassword.codeSentTo') }} {{ emailForm.email }}</p>
          <el-button type="text" class="resend-btn" @click="handleSendCode" :disabled="countdown > 0" :loading="loading">
            {{ countdown > 0 ? $t('manga.resetPassword.resendAfter', { countdown }) : $t('manga.resetPassword.resendCode') }}
          </el-button>
        </div>

        <el-form-item class="submit-item">
          <el-button class="submit-btn" type="primary" @click="handleVerifyCode" :loading="loading">
            {{ $t('manga.resetPassword.verifyBtn') }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 步骤3: 设置新密码 -->
      <el-form v-if="step === 3" class="login-form" :model="passwordForm" :rules="passwordRules" ref="passwordFormRef">
        <el-form-item prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" :placeholder="$t('manga.resetPassword.newPassword')" clearable
            class="custom-input" show-password>
            <template #prefix>
              <i class="fas fa-lock"></i>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" :placeholder="$t('manga.resetPassword.confirmPassword')" clearable
            class="custom-input" show-password @keyup.enter="handleResetPassword">
            <template #prefix>
              <i class="fas fa-lock"></i>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item class="submit-item">
          <el-button class="submit-btn" type="primary" @click="handleResetPassword" :loading="loading">
            {{ $t('manga.resetPassword.resetBtn') }}
          </el-button>
        </el-form-item>
      </el-form>

      <div class="form-footer">
        <el-button class="switch-btn" @click="goLogin" type="text">
          {{ $t('manga.resetPassword.backToLogin') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { sendEmailCode, verifyCode, resetPassword } from '@/api/auth.js'
import { localPath } from '@/i18n'

export default {
  data() {
    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.resetPassword.emailRequired')))
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        callback(new Error(this.$t('manga.resetPassword.emailInvalid')))
      } else {
        callback()
      }
    }

    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.resetPassword.passwordRequired')))
      } else if (value.length < 6) {
        callback(new Error(this.$t('manga.resetPassword.passwordMin')))
      } else {
        callback()
      }
    }

    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('manga.resetPassword.confirmRequired')))
      } else if (value !== this.passwordForm.newPassword) {
        callback(new Error(this.$t('manga.resetPassword.confirmMismatch')))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      step: 1,
      countdown: 0,
      countdownTimer: null,
      resetToken: '',
      emailForm: {
        email: ''
      },
      codeForm: {
        code: ''
      },
      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      },
      emailRules: {
        email: [{ validator: validateEmail, trigger: 'blur' }]
      },
      codeRules: {
        code: [
          { required: true, message: this.$t('manga.resetPassword.codeRequired'), trigger: 'blur' },
          { len: 6, message: this.$t('manga.resetPassword.codeLen'), trigger: 'blur' }
        ]
      },
      passwordRules: {
        newPassword: [{ validator: validatePassword, trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
      }
    }
  },
  computed: {
    stepDesc() {
      return this.$t(`manga.resetPassword.stepDesc${this.step}`)
    }
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    startCountdown() {
      this.countdown = 60
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },

    async handleSendCode() {
      const formRef = this.step === 1 ? this.$refs.emailFormRef : this.$refs.codeFormRef
      if (this.step === 1) {
        const valid = await formRef.validate().catch(() => false)
        if (!valid) return
      }

      try {
        this.loading = true
        await sendEmailCode(this.emailForm.email)
        this.$message.success(this.$t('manga.resetPassword.codeSentSuccess'))
        this.startCountdown()
        if (this.step === 1) {
          this.step = 2
        }
      } catch (e) {
        console.error('send code failed:', e)
      } finally {
        this.loading = false
      }
    },

    async handleVerifyCode() {
      const valid = await this.$refs.codeFormRef.validate().catch(() => false)
      if (!valid) return

      try {
        this.loading = true
        const { data } = await verifyCode({
          email: this.emailForm.email,
          code: this.codeForm.code
        })
        this.resetToken = data
        this.step = 3
        this.$message.success(this.$t('manga.resetPassword.verifySuccess'))
      } catch (e) {
        console.error('verify code failed:', e)
      } finally {
        this.loading = false
      }
    },

    async handleResetPassword() {
      const valid = await this.$refs.passwordFormRef.validate().catch(() => false)
      if (!valid) return

      try {
        this.loading = true
        await resetPassword({
          code: this.resetToken,
          newPassword: this.passwordForm.newPassword
        })
        this.$message.success(this.$t('manga.resetPassword.resetSuccess'))
        this.$router.push(localPath('/login'))
      } catch (e) {
        console.error('reset password failed:', e)
      } finally {
        this.loading = false
      }
    },

    goLogin() {
      this.$router.push(localPath('/login'))
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #0a0f1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('~@/assets/images/login.jpg') no-repeat center center;
  background-size: cover;
  z-index: 1;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(10, 15, 26, 0.92) 0%, rgba(15, 23, 42, 0.88) 50%, rgba(30, 41, 59, 0.85) 100%);
}

.noise-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
}

.reset-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  background: rgba(15, 23, 42, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 20px;
  padding: 2rem 1.8rem;
}

.login-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1rem;
}

.logo-img {
  height: 2rem;
  width: auto;
  filter: drop-shadow(0 2px 10px rgba(94, 234, 212, 0.3));
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  letter-spacing: 1px;
}

.login-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.3rem 0;
}

.login-subtitle {
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: rgba(30, 41, 59, 0.8);
  border: 2px solid rgba(94, 234, 212, 0.2);
  color: #64748b;
  transition: all 0.3s ease;
}

.step.active .step-dot {
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  border-color: #5eead4;
  color: #0a0f1a;
}

.step.completed .step-dot {
  background: #5eead4;
  border-color: #5eead4;
  color: #0a0f1a;
}

.step span {
  font-size: 0.7rem;
  color: #64748b;
  white-space: nowrap;
}

.step.active span {
  color: #5eead4;
}

.step-line {
  flex: 1;
  height: 2px;
  background: rgba(94, 234, 212, 0.12);
  margin: 0 8px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.step-line.active {
  background: linear-gradient(90deg, #5eead4, #a78bfa);
}

.login-form {
  margin-bottom: 1rem;
}

.custom-input {
  margin-bottom: 0.8rem;
}

:deep(.custom-input .el-input__wrapper) {
  background: rgba(30, 41, 59, 0.6) !important;
  border: 1px solid rgba(94, 234, 212, 0.12) !important;
  border-radius: 12px !important;
  padding: 4px 12px !important;
  transition: all 0.3s ease !important;
  box-shadow: none !important;
}

:deep(.custom-input .el-input__wrapper:hover) {
  border-color: rgba(94, 234, 212, 0.3) !important;
}

:deep(.custom-input .el-input__wrapper.is-focus) {
  border-color: #5eead4 !important;
  box-shadow: 0 0 0 3px rgba(94, 234, 212, 0.08) !important;
}

:deep(.custom-input .el-input__inner) {
  font-size: 0.9rem !important;
  color: #e2e8f0 !important;
}

:deep(.custom-input .el-input__inner::placeholder) {
  color: #64748b !important;
}

:deep(.custom-input .el-input__prefix) {
  color: #5eead4 !important;
}

:deep(.custom-input .el-input__password) {
  color: #5eead4 !important;
}

.submit-item {
  margin-bottom: 0.5rem !important;
}

.submit-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(135deg, #5eead4, #a78bfa) !important;
  border: none !important;
  border-radius: 12px !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  color: #0a0f1a !important;
  transition: all 0.3s ease !important;
  letter-spacing: 2px;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(94, 234, 212, 0.25) !important;
}

.submit-btn.is-disabled {
  opacity: 0.6 !important;
  transform: none !important;
}

.code-hint {
  text-align: center;
  margin-bottom: 1rem;
}

.code-hint p {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0 0 0.3rem 0;
}

.resend-btn {
  color: #5eead4 !important;
  font-size: 0.78rem !important;
  padding: 0 !important;
  height: auto !important;
}

.resend-btn:hover {
  color: #a78bfa !important;
  background: none !important;
}

.resend-btn.is-disabled {
  color: #64748b !important;
}

.form-footer {
  text-align: center;
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid rgba(94, 234, 212, 0.08);
}

.switch-btn {
  color: #5eead4 !important;
  font-size: 0.78rem !important;
  padding: 0 !important;
  height: auto !important;
}

.switch-btn:hover {
  color: #a78bfa !important;
  background: none !important;
}

@media (max-width: 480px) {
  .reset-card {
    padding: 1.5rem 1.2rem;
  }

  .step-indicator {
    padding: 0;
  }

  .step span {
    font-size: 0.65rem;
  }

  .brand-title {
    font-size: 1.3rem;
  }

  .login-title {
    font-size: 1.2rem;
  }
}
</style>
